import { XIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { twMerge } from 'tailwind-merge';
import { UnsplashImage } from '../../../types/blogTypes';
import { blogApiClient } from '../../clientUtils/blogApiClient';
import { useMobxInstance } from '../../clientUtils/mobxUtil';
import _ from 'lodash';

class ViewStore {
    searchText = '';
    selectedImages: UnsplashImage[] = [];
    searchedImages: UnsplashImage[] = [];

    constructor() {
        makeAutoObservable(this);
    }

    setSearchText(value: string) {
        this.searchText = value;
    }

    async search() {
        const images = await blogApiClient.blogPost.searchBlogPostImage.$query({
            lang: 'ko',
            query: this.searchText,
        });

        runInAction(() => {
            this.searchedImages = images;
        });
    }

    selectImage(image: UnsplashImage) {
        this.selectedImages.push(image);
        blogApiClient.blogPost.trackUnsplashImageDownload.mutate({
            downloadLocation: image.links.download_location,
        });
    }

    removeImage(imageId: string) {
        this.selectedImages = this.selectedImages.filter((i) => i.id !== imageId);
    }
}

const UnsplashImageCard = observer(
    ({
        image,
        imageClassName,
        onClickImage,
        onClickClose,
    }: {
        image: UnsplashImage;
        imageClassName?: string;
        onClickImage?: (image: UnsplashImage) => void;
        onClickClose?: (imageId: string) => void;
    }) => {
        return (
            <div className="relative flex flex-col gap-2">
                <img
                    loading="lazy"
                    src={image.urls.thumb}
                    className={twMerge(
                        'h-30 w-full cursor-pointer rounded object-cover',
                        imageClassName,
                    )}
                    onClick={(e) => {
                        e.preventDefault();
                        onClickImage?.(image);
                    }}
                />
                <div className="text-xs text-gray-500">
                    <a
                        className="link link-hover"
                        href={`${image.user.links.html}?utm_source=retirein40s&utm_medium=referral`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {image.user.username}
                    </a>
                </div>
                {onClickClose && (
                    <button
                        className="btn btn-ghost btn-xs btn-circle absolute top-2 right-2 z-10 bg-white/50"
                        onClick={() => {
                            onClickClose?.(image.id);
                        }}
                    >
                        <XIcon className="h-3 w-3" />
                    </button>
                )}
            </div>
        );
    },
);

export const BlogImageSearch = observer(
    ({ onClickSave }: { onClickSave: (images: string[]) => void }) => {
        const viewStore = useMobxInstance(ViewStore);

        return (
            <div className="flex flex-col gap-2">
                <div className="grid grid-cols-4 gap-2">
                    {viewStore.selectedImages.map((image) => (
                        <UnsplashImageCard
                            key={image.id}
                            image={image}
                            imageClassName="h-30 w-full cursor-pointer rounded object-cover"
                            onClickClose={(imageId) => {
                                viewStore.removeImage(imageId);
                            }}
                        />
                    ))}
                </div>
                <div className="flex flex-row items-center justify-between">
                    <div className="text-sm text-gray-500">
                        {_.isEmpty(viewStore.selectedImages) ? 'Search and select images.' : ''}
                    </div>
                    <button
                        className={twMerge(
                            'btn btn-primary btn-sm btn-wide',
                            _.isEmpty(viewStore.selectedImages) && 'btn-disabled',
                        )}
                        onClick={() => {
                            onClickSave(viewStore.selectedImages.map((i) => i.urls.regular));
                        }}
                    >
                        Save Images
                    </button>
                </div>
                <div className="card border-base-300 mt-4 w-full border">
                    <div className="card-body">
                        <fieldset className="fieldset">
                            <legend className="fieldset-legend">Image Search</legend>

                            <div className="join">
                                <input
                                    type="text"
                                    placeholder="Search for images"
                                    className="input join-item"
                                    value={viewStore.searchText}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        viewStore.setSearchText(value);
                                    }}
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === 'Enter' &&
                                            e.nativeEvent.isComposing === false
                                        ) {
                                            e.preventDefault();
                                            viewStore.search();
                                        }
                                    }}
                                />
                                <button
                                    className="btn join-item"
                                    onClick={() => {
                                        viewStore.search();
                                    }}
                                >
                                    Search
                                </button>
                            </div>
                        </fieldset>
                        <div className="grid grid-cols-4 gap-2">
                            {viewStore.searchedImages.map((image) => (
                                <UnsplashImageCard
                                    key={image.id}
                                    image={image}
                                    imageClassName="h-30 w-full cursor-pointer rounded object-cover"
                                    onClickImage={(image) => {
                                        viewStore.selectImage(image);
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);
